import { z } from 'zod';

import { smartNullish } from '@elara/core/zod-utils';
import { zFirestampNow, zFirestampOptional } from '@elara/firebolt/zod-types';

import { WidgetImageSchema } from './image-schemas.js';
import {
  AddressSchema,
  ContactSchema,
  CoordinatesSchema,
  isCoordinates
} from './shared-schemas.js';

/**
 * Brand information schema
 */
export const BrandSchema = z.object({
  chainCode: z.string().optional(),
  chainName: z.string().optional(),
  code: z.string().optional(),
  logo: z.string().url().optional(),
  name: z.string(),
});

export interface Brand extends z.infer<typeof BrandSchema> {}

/**
 * Sabre's aggregator pseudo-chains: 7E Expedia, 7B Booking.com, 7U Nuitee, HQ HRS. A record under one of
 * these was imported from that aggregator's listing rather than from a hotel chain, so `brand.logo` is
 * the aggregator's logo and `brand.name` is empty — neither describes the hotel.
 */
export const AGGREGATOR_CHAIN_CODES: ReadonlySet<string> = new Set(['7E', '7B', '7U', 'HQ']);

/** True when the brand block is an aggregator placeholder, not a hotel brand (see {@link AGGREGATOR_CHAIN_CODES}). */
export function isAggregatorBrand(brand: { chainCode?: string | null } | null | undefined): boolean {
  return !!brand?.chainCode && AGGREGATOR_CHAIN_CODES.has(brand.chainCode);
}

/**
 * Destination reference schema
 */
export const DestinationReferenceSchema = z.object({
  id: z.string(),
  name: z.string(),
  // Real values are ISO-2 country codes. Enrichment writes a "NO_MATCH" sentinel when it can't
  // resolve a destination; that value (and any other non-2-char junk) would otherwise fail the
  // whole hotel's parse and blank its recommendation card / block shortlisting. Degrade an invalid
  // code to undefined instead of throwing so the hotel still renders. This is the Firestore read
  // path only — the ES search projection keeps the raw value, so NO_MATCH stays findable.
  countryCode: z.string().length(2).nullish().catch(undefined),
  travelRegion: z.string().nullish(),
});

export interface DestinationReference extends z.infer<typeof DestinationReferenceSchema> {}

/**
 * Hotel IDs from various systems
 */
export const HotelIDsSchema = z
  .object({
    global: z.string().nullish(),
    /** @deprecated Use ids.global instead */
    sabre: z.string().describe('Deprecated: Use ids.global instead for Sabre ID').nullish(),
    /** Travelport property id: `${chainCode}-${propertyCode}` composite, e.g. "DT-35429" */
    travelport: z.string().nullish(),
    google: z.string().nullish(),
    tripadvisor: z.string().nullish(),
  })
  .passthrough();

export interface HotelIDs extends z.infer<typeof HotelIDsSchema> {}

const ZodLatitude = z.number();
const ZodLongitude = z.number();

const CoordinatesWithMetadataSchema = z.preprocess(
  (data: unknown) => {
    // Handle Firestore GeoPoint format with latitude/longitude properties
    if (data && typeof data === 'object' && 'latitude' in data && 'longitude' in data) {
      const geoPoint = data as { latitude: number; longitude: number };
      return { lat: geoPoint.latitude, lng: geoPoint.longitude };
    }
    // Return data as-is for regular lat/lng format
    return data;
  },
  z.object({
    lat: z.number(),
    lng: z.number(),
    address: z.string().nullish(),
    city: z.string().nullish(),
    country: z.string().nullish(),
    coordinates: smartNullish(CoordinatesSchema),
  }),
);
export interface CoordinatesWithMetadata extends z.infer<typeof CoordinatesWithMetadataSchema> {}

const LegacyCoordinatesSchema = z
  .object({ __lat__: ZodLatitude, __lng__: ZodLongitude })
  .passthrough()
  .transform(({ __lat__: lat, __lng__: lng }) => ({ lat, lng }));
export interface LegacyCoordinates extends z.infer<typeof LegacyCoordinatesSchema> {}

const FirestoreSerializedGeoPointSchema = z
  .object({ _latitude: ZodLatitude, _longitude: ZodLongitude })
  .passthrough()
  .transform(({ _latitude: lat, _longitude: lng }) => ({ lat, lng }));
export interface FirestoreSerializedGeoPoint extends z.infer<typeof FirestoreSerializedGeoPointSchema> {}

export const FirestoreGeoPointSchema = z
  .object({ latitude: ZodLatitude, longitude: ZodLongitude })
  .passthrough()
  .transform(({ latitude: lat, longitude: lng }) => ({ lat, lng }));
export interface FirestoreGeoPoint extends z.infer<typeof FirestoreGeoPointSchema> {}

type LocationCandidate = CoordinatesWithMetadata | LegacyCoordinates | FirestoreSerializedGeoPoint | FirestoreGeoPoint;

function normalizeHotelLocation(location: LocationCandidate) {
  const { lat, lng, coordinates, ...meta } = location as Record<string, unknown>;
  const normalized: Record<string, unknown> = { lat, lng, ...meta };

  if (isCoordinates(coordinates)) {
    normalized.coordinates = CoordinatesSchema.parse({ lat: coordinates.lat, lng: coordinates.lng });
  }

  return CoordinatesWithMetadataSchema.parse(normalized);
}

export const HotelLocationSchema = z
  .union([
    CoordinatesWithMetadataSchema,
    LegacyCoordinatesSchema,
    FirestoreSerializedGeoPointSchema,
    FirestoreGeoPointSchema,
  ])
  .transform((location) => normalizeHotelLocation(location));

export interface HotelLocation extends z.infer<typeof HotelLocationSchema> {}

/**
 * Property details schema
 */
export const PropertyDetailsSchema = z.object({
  type: z.string().optional(), // e.g., "Hotel", "Resort", "Motel"
  quality: z.string().optional(), // e.g., "Luxury", "Standard"
  rooms: z.number().optional(),
  floors: z.number().optional(),
  checkIn: z.string().optional(), // Time in HHMM format
  checkOut: z.string().optional(), // Time in HHMM format
});

export interface PropertyDetails extends z.infer<typeof PropertyDetailsSchema> {}

/**
 * Room Type Schema (backward compatibility)
 */
export const RoomTypeSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  type: z.string().optional(),
  description: z.string().optional(),
  maxOccupancy: z.number().positive().optional(),
  bedConfiguration: z.string().optional(),
  size: z.object({ value: z.number().positive(), unit: z.enum(['sqft', 'sqm']) }).optional(),
  baseRate: z.number().optional(),
  amenities: z.array(z.string()).optional(),
  images: z.array(z.string().url()).optional(),
  available: z.boolean().optional(),
  quantity: z.number().optional(),
});

export interface RoomType extends z.infer<typeof RoomTypeSchema> {}

/**
 * Hotel Affiliation Schema
 * Represents membership in consortia, loyalty programs, or partnerships
 */
export const HotelAffiliationSchema = z.object({
  name: z.string(),
  type: z.string().nullish(),
  benefits: z.string().nullish(),
  verified_at: zFirestampNow().optional(),
  /** Canonical 15-char Salesforce Account id (serandipians) — the join key for member-portal
   *  lookups. Written by serandipiansSync; MUST be declared here or model reads strip it
   *  (which silently disabled the contacts resolver's Serandipians source). */
  externalId: z.string().nullish(),
  /** Hash of the raw benefits source text the stored `benefits` was rewritten from (serandipiansSync change detection). */
  benefitsSourceHash: z.string().nullish(),
});

export interface HotelAffiliation extends z.infer<typeof HotelAffiliationSchema> {}

/**
 * Snapshot of the most recent pricing data retrieved from Sabre or other providers.
 */
export const LatestPricingSnapshotSchema = z
  .object({
    roomDetails: z
      .object({ id: z.string().optional(), type: z.string().optional(), description: z.string().optional() })
      .optional(),
    lowestRate: z
      .object({
        currency: z.string().optional(),
        averageNightlyRate: z.number().nonnegative().optional(),
        totalPrice: z.number().nonnegative().optional(),
      })
      .optional(),
    stayDates: z.object({ start: z.string(), end: z.string() }).optional(),
  })
  .passthrough();

export interface LatestPricingSnapshot extends z.infer<typeof LatestPricingSnapshotSchema> {}

export const HotelPricingSchema = z.object({
  currency: z.string().nullish(),
  latest: LatestPricingSnapshotSchema.nullish(),
});

export interface HotelPricing extends z.infer<typeof HotelPricingSchema> {}

/**
 * A single lifecycle-status signal from one source, recorded as provenance.
 *
 * A hotel's lifecycle status is corroborated across multiple signals rather than
 * trusted from any one source. `fresh` and `agrees` let the gate down-weight a
 * stale or dissenting source -- e.g. an OTA still quoting bookable rates for a
 * hotel that has already been demolished.
 */
export const HotelStatusSignalSchema = z.looseObject({
  source: z.enum([
    'google_places',
    'official_site',
    'tripadvisor',
    'serpapi_google_hotels',
    'web_search',
    'news',
    'operator',
  ]),
  field: z.string().describe('The source field observed, e.g. "businessStatus", "rates", "rating".'),
  value: z.string().describe('What the source indicated, e.g. "CLOSED_PERMANENTLY", "$773/night bookable".'),
  agrees: z.boolean().nullish().describe('Whether this signal agrees with the resolved status.'),
  fresh: z.enum(['fresh', 'stale', 'unknown']).nullish(),
  evidence: z.string().nullish().describe('Canonical source URL for the observation.'),
  observedAt: zFirestampNow(),
});

export interface HotelStatusSignal extends z.infer<typeof HotelStatusSignalSchema> {}

const HotelStatusConfidenceSchema = z.enum(['low', 'medium', 'high']);

/**
 * Fields shared by every lifecycle variant. Spread into each discriminated-union
 * member below so the corroboration apparatus (signals, confidence) and the
 * free-text operator note live on all statuses.
 */
const hotelLifecycleCommonShape = {
  /** Free-text rationale from an operator. The enum `statusReason` is the queryable handle; this carries the story. */
  statusNote: z.string().nullish(),
  signals: z.array(HotelStatusSignalSchema).nullish(),
  confidence: HotelStatusConfidenceSchema.nullish().describe('Agreement across fresh, authoritative signals.'),
  /**
   * When the automated status gate last evaluated this hotel (regardless of whether
   * the status changed). Top-level + queryable so the recurring recheck can select
   * "hotels not checked in the last N days" -- the per-signal `observedAt` lives inside
   * the `signals[]` array and can't be range-queried.
   */
  // Absent until the gate first evaluates the hotel — must NOT default to now
  // (else a never-checked hotel falsely reads as just-checked, breaking the
  // recheck-due query). The gate always sets it explicitly on a real run.
  statusCheckedAt: zFirestampOptional().describe(
    'When the automated status gate last evaluated this hotel. Drives periodic recheck.',
  ),
};

/**
 * Hotel lifecycle status -- the source of truth for whether a property is
 * operating. Aligned to the OpenTravel HotelStatusCode (HOS) concept; `pre_opening`
 * maps to OpenTravel's "PreOpening" value. Modeled as a discriminated union on
 * `status` so illegal combinations cannot be represented (e.g. a permanently_closed
 * hotel cannot carry an expectedOpening; a rebranded hotel can name its successor).
 */
export const HotelLifecycleSchema = z.discriminatedUnion('status', [
  // Imported but not yet vetted as a real, operating property (replaces the old `verified` intent).
  z.looseObject({ status: z.literal('candidate'), statusReason: z.string().nullish(), ...hotelLifecycleCommonShape }),
  // Open and bookable.
  z.looseObject({ status: z.literal('active'), statusReason: z.string().nullish(), ...hotelLifecycleCommonShape }),
  // Announced but not yet operating.
  z.looseObject({
    status: z.literal('pre_opening'),
    expectedOpening: z.string().nullish().describe('Provisional opening date or year, e.g. "2027" -- re-verifiable.'),
    ...hotelLifecycleCommonShape,
  }),
  z.looseObject({
    status: z.literal('temporarily_closed'),
    statusReason: z.enum(['renovation', 'seasonal', 'force_majeure', 'unknown']),
    ...hotelLifecycleCommonShape,
  }),
  z.looseObject({
    status: z.literal('permanently_closed'),
    statusReason: z.enum(['closed', 'demolished']),
    ...hotelLifecycleCommonShape,
  }),
  z.looseObject({
    status: z.literal('rebranded'),
    statusReason: z.enum(['reflagged', 'merged']),
    previousName: z.string().nullish(),
    successorHotelId: z.string().nullish(),
    ...hotelLifecycleCommonShape,
  }),
  // Deliberately pulled by Elara (quality, duplicate, Sabre test record, out of scope).
  z.looseObject({
    status: z.literal('removed'),
    statusReason: z.enum(['quality', 'duplicate', 'test_record', 'out_of_scope']),
    ...hotelLifecycleCommonShape,
  }),
]);

export type HotelLifecycle = z.infer<typeof HotelLifecycleSchema>;
export type HotelLifecycleStatus = HotelLifecycle['status'];

/**
 * A single proposed change to a hotel field, pending operator review.
 */
export const HotelProposedChangeSchema = z.looseObject({
  field: z
    .string()
    .regex(
      /^[A-Za-z_][\w]*(\.[A-Za-z_][\w]*)*$/,
      'Must be a dot-separated field path of identifier segments (e.g. "lifecycle.status").',
    )
    .describe('The field a change is proposed for, e.g. "lifecycle.status", "name", "ids.google".'),
  currentValue: z.string().nullish(),
  proposedValue: z.string(),
});

export interface HotelProposedChange extends z.infer<typeof HotelProposedChangeSchema> {}

/**
 * A proposed change to a hotel's lifecycle awaiting confirmation. Kept separate
 * from `lifecycle` so the current status stays live while review is pending --
 * an automated signal proposes, an operator disposes. Absent = nothing to review.
 */
export const HotelPendingReviewSchema = z.looseObject({
  reason: z.enum(['status_transition', 'identity_drift', 'rebrand_suspected', 'low_confidence_match', 'closed_observed']),
  changes: z.array(HotelProposedChangeSchema).min(1),
  detectedBy: z.enum(['ids_status_gate', 'aggregator_validation', 'operator', 'travelport_match']),
  detectedAt: zFirestampNow(),
  confidence: HotelStatusConfidenceSchema.nullish(),
  signals: z.array(HotelStatusSignalSchema).nullish().describe('The signals that triggered this proposed change.'),
  // Absent until an operator resolves it — must NOT default to now (zFirestampNow's
  // default fires on parse and would make every fresh proposal look resolved).
  resolvedAt: zFirestampOptional(),
  resolution: z.enum(['accepted', 'rejected', 'superseded']).nullish(),
});

export interface HotelPendingReview extends z.infer<typeof HotelPendingReviewSchema> {}

/**
 * Hotel Data Schema
 * Matches the actual Firestore data structure from production
 */
export const HotelDataSchema = z.looseObject({
  // Core identification
  id: z.string().optional(), // Document ID, optional for creation
  name: z.string().min(1),
  ids: HotelIDsSchema.nullish(),

  // Location information - normalized to { lat, lng }
  address: AddressSchema.nullish(),
  location: HotelLocationSchema.nullish(),
  destination: DestinationReferenceSchema.nullish(),

  // Hotel details
  rating: z.number().min(1).max(5).nullish(),
  ratings: z
    .object({
      forbes: z.number().min(1).max(5).nullish(),
      michelin: z.boolean().nullish(),
      virtuoso: z.boolean().nullish(),
      lhw: z.boolean().nullish(), // Leading Hotels of the World
      designHotels: z.boolean().nullish(),
      andrewHarper: z.boolean().nullish(),
      condeNast: z.boolean().nullish(),
      travelAndLeisure: z.boolean().nullish(),
    })
    .nullish(),
  brand: BrandSchema.nullish(),
  property: PropertyDetailsSchema.nullish(),

  // Features and amenities
  amenities: smartNullish(z.array(z.string())),
  securityFeatures: z.array(z.string()).nullish(),
  uniqueFeatures: z.array(z.string()).nullish(),
  images: z.array(WidgetImageSchema).nullish(), // Can be URLs or paths
  // Provenance of the stored photo set (head `images` + the media/gallery subdoc).
  // Written by ensureHotelPhotos; absence means photos were never fetched for this hotel.
  imagesMeta: z
    .object({
      /** Provider that supplied the photos (open string: "sabre", "travelport", ... or "none"). */
      source: z.string(),
      /** Total images stored for this hotel (the gallery subdoc when present, else head `images`). */
      total: z.number(),
      fetchedAt: zFirestampOptional(),
      /** Provider sources that were eligible when fetched — lets a "none" outcome retry when a NEW provider becomes eligible. */
      providers: z.array(z.string()).optional(),
    })
    .nullish(),

  // Contact and web presence. `contact`/`concierge` are projections of the private
  // contacts directory (hotels/{id}/contacts/directory) once the resolver has run —
  // only whitelist-public types ever land here (see projectPublicContacts).
  contact: ContactSchema.nullish(),
  website: z.string().url().nullish(),
  // Provenance of the stored contact directory (values-free — the head doc is readable by
  // every authenticated user, so contact VALUES beyond the public projection never live
  // here). Written operationally by the contacts resolver; absence = never resolved.
  contactsMeta: z
    .object({
      fetchedAt: zFirestampOptional(),
      /** Which sources contributed entries (e.g. ["web_extraction", "legacy"]). */
      sources: z.array(z.string()).optional(),
      /** n8n's 0–6 completeness rubric. */
      enrichmentScore: z.number().optional(),
      /** Total directory entries stored. */
      total: z.number().optional(),
    })
    .nullish(),

  // Room types
  roomTypes: z.array(RoomTypeSchema).nullish(),
  pricing: HotelPricingSchema.nullish(),

  // Policies (backward compatibility)
  policies: z
    .object({
      checkIn: z.string(),
      checkOut: z.string(),
      cancellation: z.string(),
      childPolicy: z.string().optional(),
      petPolicy: z.string().nullish(),
    })
    .nullish(),

  // Affiliations (consortia, partnerships, etc.)
  affiliations: z.record(z.string(), HotelAffiliationSchema).nullish(),

  // Hotel concierge contact
  concierge: z
    .looseObject({
      name: z.string().nullish(),
      phone: z.string().nullish(),
      email: z.string().nullish(),
      notes: z.string().nullish(),
    })
    .nullish(),

  // Team notes: internalNotes stays private to the admin; elaraNotes reaches the agents (capped at 400 here, in the admin editor, and wherever it is read)
  internalNotes: z.string().nullish().describe('Private admin notes not visible to users or agents'),
  elaraNotes: z
    .string()
    .max(400)
    .nullish()
    .describe("The team's own note on the hotel, for the team and the agents. Internal, never shown to travelers."),

  // Metadata
  description: z.string().nullish(),
  short_description: z.string().nullish(),
  long_description: z.string().nullish(),
  dining_detail: z.string().nullish(),
  experiences_detail: z.string().nullish(),
  accommodations_detail: z.string().nullish(),
  family_detail: z.string().nullish(),
  contentLinks: z
    .array(
      z.object({ sourceUrl: z.string().nullish(), source: z.string().nullish(), sourceType: z.string().nullish() }),
    )
    .nullish(),
  // Lifecycle status (OpenTravel HotelStatusCode-aligned) -- the source of truth
  // for whether a property is operating. `isActive` will be derived from this.
  lifecycle: HotelLifecycleSchema.nullish(),
  // A proposed change to `lifecycle` awaiting confirmation, kept separate so the
  // current status stays live while review is pending. Absent = nothing to review.
  pendingReview: HotelPendingReviewSchema.nullish(),
  verified: z.boolean().nullish(),
  verificationSource: z.string().nullish(),
  /** Whether this hotel is active and visible in search results. Treated as true when absent. */
  isActive: z.boolean().default(true),

  // External IDs (backward compatibility)
  amadeusId: z.string().describe('Deprecated: Use ids.amadeus instead for Amadeus ID').nullish(),
  /** @deprecated Use ids.global instead */
  sabreId: z.string().describe('Deprecated: Use ids.global instead for Sabre ID').nullish(),

  // Timestamps
  createdAt: zFirestampNow().optional(),
  updatedAt: zFirestampNow().optional(),
  lastUpdated: zFirestampNow().optional(),
});

export interface HotelData extends z.infer<typeof HotelDataSchema> {}

/**
 * Hotel Description Subcollection Schema
 * Represents multilingual descriptions stored in subcollections
 */
export const HotelDescriptionSchema = z.object({
  type: z.string(), // e.g., "Dining", "Overview", "Amenities"
  content: z.string(),
  source: z.string().optional(), // e.g., "sabre"
  language: z.string().optional(), // ISO language code
});

export interface HotelDescription extends z.infer<typeof HotelDescriptionSchema> {}

/**
 * Hotel Media Subcollection Schema
 * Represents media assets stored in subcollections
 */
export const HotelMediaSchema = z.object({
  url: z.string().url(),
  caption: z.string().optional(),
  category: z.string().optional(), // e.g., "Guest room", "Exterior"
  mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
  format: z.string().optional(), // e.g., "ORIGINAL", "THUMBNAIL"
  roomTypeCodes: z.array(z.string()).optional(),
  order: z.number().optional(),
});

export interface HotelMedia extends z.infer<typeof HotelMediaSchema> {}

